import { apiRequest } from '../../../shared/services/apiClient';
import {
  CreatePaymentRequest,
  OPEN_PAYMENT_STATUSES,
  Payment,
  PaymentAutomationActionType,
  PaymentAutomationRule,
  PaymentAutomationTrigger,
  PaymentEvent,
  PaymentListQuery,
  PaymentPagedResult,
  PaymentSettings,
  PaymentStatus,
  SendPaymentLinkRequest,
  UpdatePaymentSettingsRequest,
  UpsertPaymentAutomationRuleRequest,
} from '../types/Payment';

const STATUSES: PaymentStatus[] = [
  'Pending',
  'AwaitingCustomer',
  'Paid',
  'Failed',
  'Expired',
  'Canceled',
];

const TRIGGERS: PaymentAutomationTrigger[] = ['paid'];
const ACTIONS: PaymentAutomationActionType[] = ['conversation_message', 'create_task'];

const optionalText = (value: unknown): string | undefined => {
  const text = String(value ?? '').trim();
  return text || undefined;
};

const asNumber = (value: unknown, fallback = 0): number => {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeEnum = <T extends string>(value: unknown, allowed: T[], fallback: T): T => {
  const text = String(value ?? '');
  return allowed.includes(text as T) ? text as T : fallback;
};

const pickItems = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    if (Array.isArray(record.items)) return record.items;
    if (Array.isArray(record.data)) return record.data;
  }
  return [];
};

const pickSingle = (payload: unknown): Record<string, unknown> | null => {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  if (record.data && typeof record.data === 'object' && !Array.isArray(record.data)) {
    return record.data as Record<string, unknown>;
  }
  return record;
};

const buildQuery = (path: string, query?: Record<string, string | number | boolean | undefined>): string => {
  if (!query) return path;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    params.set(key, String(value));
  });
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
};

const normalizeEvent = (value: unknown): PaymentEvent => {
  const item = (value ?? {}) as Record<string, unknown>;
  return {
    id: String(item.id ?? ''),
    fromStatus: optionalText(item.fromStatus),
    toStatus: String(item.toStatus ?? ''),
    providerStatus: optionalText(item.providerStatus),
    note: optionalText(item.note),
    createdAtUtc: String(item.createdAtUtc ?? ''),
  };
};

const normalizePayment = (value: unknown): Payment => {
  const item = (value ?? {}) as Record<string, unknown>;
  return {
    id: String(item.id ?? ''),
    amount: asNumber(item.amount),
    currency: String(item.currency ?? 'AZN'),
    description: String(item.description ?? ''),
    status: normalizeEnum(item.status, STATUSES, 'Pending'),
    providerCode: String(item.providerCode ?? ''),
    checkoutUrl: optionalText(item.checkoutUrl),
    providerStatus: optionalText(item.providerStatus),
    contactId: optionalText(item.contactId),
    conversationId: optionalText(item.conversationId),
    invoiceId: optionalText(item.invoiceId),
    expiresAtUtc: optionalText(item.expiresAtUtc),
    paidAtUtc: optionalText(item.paidAtUtc),
    createdAtUtc: String(item.createdAtUtc ?? ''),
    updatedAtUtc: String(item.updatedAtUtc ?? ''),
    events: Array.isArray(item.events) ? item.events.map(normalizeEvent) : [],
  };
};

const normalizeSettings = (value: unknown): PaymentSettings => {
  const item = (value ?? {}) as Record<string, unknown>;
  return {
    enabled: Boolean(item.enabled),
    providerCode: optionalText(item.providerCode),
    hasCredentials: Boolean(item.hasCredentials),
    currency: String(item.currency ?? 'AZN'),
    language: String(item.language ?? 'az'),
    environment: String(item.environment ?? 'live'),
    returnPath: optionalText(item.returnPath),
    availableProviders: Array.isArray(item.availableProviders)
      ? item.availableProviders.map(code => String(code))
      : ['pasha'],
  };
};

const normalizeAutomation = (value: unknown): PaymentAutomationRule => {
  const item = (value ?? {}) as Record<string, unknown>;
  return {
    id: String(item.id ?? ''),
    name: String(item.name ?? ''),
    isEnabled: Boolean(item.isEnabled),
    trigger: normalizeEnum(item.trigger, TRIGGERS, 'paid'),
    actionType: normalizeEnum(item.actionType, ACTIONS, 'conversation_message'),
    templateText: optionalText(item.templateText),
    taskTitle: optionalText(item.taskTitle),
    taskDescription: optionalText(item.taskDescription),
    createdAtUtc: String(item.createdAtUtc ?? ''),
    updatedAtUtc: String(item.updatedAtUtc ?? ''),
  };
};

export const isOpenPayment = (status: PaymentStatus): boolean =>
  OPEN_PAYMENT_STATUSES.includes(status);

export const paymentService = {
  getSettings: async (): Promise<PaymentSettings> =>
    normalizeSettings(pickSingle(await apiRequest<unknown>('/api/v1/payments/settings'))),

  updateSettings: async (request: UpdatePaymentSettingsRequest): Promise<PaymentSettings> =>
    normalizeSettings(pickSingle(await apiRequest<unknown>('/api/v1/payments/settings', {
      method: 'PUT',
      body: JSON.stringify(request),
    }))),

  list: async (query?: PaymentListQuery): Promise<PaymentPagedResult> => {
    const payload = await apiRequest<Record<string, unknown>>(buildQuery('/api/v1/payments', {
      status: query?.status,
      contactId: query?.contactId,
      conversationId: query?.conversationId,
      page: query?.page,
      pageSize: query?.pageSize,
    }));
    const items = pickItems(payload).map(normalizePayment);
    return {
      items,
      page: asNumber(payload?.page, query?.page ?? 1),
      pageSize: asNumber(payload?.pageSize, query?.pageSize ?? items.length),
      total: asNumber(payload?.total, items.length),
      hasNext: Boolean(payload?.hasNext),
    };
  },

  getById: async (id: string): Promise<Payment> =>
    normalizePayment(pickSingle(await apiRequest<unknown>(`/api/v1/payments/${id}`))),

  create: async (request: CreatePaymentRequest): Promise<Payment> =>
    normalizePayment(pickSingle(await apiRequest<unknown>('/api/v1/payments', {
      method: 'POST',
      body: JSON.stringify(request),
    }))),

  sync: async (id: string): Promise<Payment> =>
    normalizePayment(pickSingle(await apiRequest<unknown>(`/api/v1/payments/${id}/sync`, {
      method: 'POST',
    }))),

  cancel: async (id: string): Promise<Payment> =>
    normalizePayment(pickSingle(await apiRequest<unknown>(`/api/v1/payments/${id}/cancel`, {
      method: 'POST',
    }))),

  send: async (id: string, request?: SendPaymentLinkRequest): Promise<Payment> =>
    normalizePayment(pickSingle(await apiRequest<unknown>(`/api/v1/payments/${id}/send`, {
      method: 'POST',
      body: JSON.stringify(request ?? {}),
    }))),

  listAutomations: async (): Promise<PaymentAutomationRule[]> =>
    pickItems(await apiRequest<unknown>('/api/v1/payments/automations')).map(normalizeAutomation),

  createAutomation: async (request: UpsertPaymentAutomationRuleRequest): Promise<PaymentAutomationRule> =>
    normalizeAutomation(pickSingle(await apiRequest<unknown>('/api/v1/payments/automations', {
      method: 'POST',
      body: JSON.stringify(request),
    }))),

  updateAutomation: async (id: string, request: UpsertPaymentAutomationRuleRequest): Promise<PaymentAutomationRule> =>
    normalizeAutomation(pickSingle(await apiRequest<unknown>(`/api/v1/payments/automations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }))),

  deleteAutomation: async (id: string): Promise<void> => {
    await apiRequest<unknown>(`/api/v1/payments/automations/${id}`, { method: 'DELETE' });
  },
};

export const getPaymentSettings = paymentService.getSettings;
export const updatePaymentSettings = paymentService.updateSettings;
export const listPayments = paymentService.list;
export const getPaymentById = paymentService.getById;
export const createPaymentLink = paymentService.create;
export const syncPayment = paymentService.sync;
export const cancelPayment = paymentService.cancel;
export const sendPaymentLink = paymentService.send;
export const listPaymentAutomations = paymentService.listAutomations;
export const createPaymentAutomation = paymentService.createAutomation;
export const updatePaymentAutomation = paymentService.updateAutomation;
export const deletePaymentAutomation = paymentService.deleteAutomation;
